import { ArrowCounterClockwiseIcon, CheckIcon, Icon } from '@phosphor-icons/react'
import { TFunction } from 'i18next'
import { useEffect } from 'react'

import { ChecklistBulkStatus } from '@/api/checklists/bulk-update-status-checklists'
import { BadgeColorKeys } from '@/components/ui/badge'
import { useFilters, useSearchParams } from '@/hooks'
import { ChecklistStatus, ChecklistType } from '@/models/features'

import type { ChecklistsFilters } from './checklists-dashboard.page.constants'
import { ChecklistStatusExtended } from './checklists-dashboard.page.types'

const CHECKLISTS_STATUS_COLOR_MAP: Record<ChecklistStatus, BadgeColorKeys> = {
  completed: 'green',
  'not-started': 'neutral',
  'past-due': 'red',
  'pending-review': 'orange',
}

export const getChecklistStatusColor = (status: ChecklistStatus) => {
  return CHECKLISTS_STATUS_COLOR_MAP[status]
}

export const getChecklistStatusLabel = (
  status: ChecklistStatus,
  t: TFunction<['checklists']>,
): string => {
  const labels: Record<ChecklistStatus, string> = {
    completed: t('checklists:dashboard.status.completed'),
    'not-started': t('checklists:dashboard.status.notStarted'),
    'past-due': t('checklists:dashboard.status.pastDue'),
    'pending-review': t('checklists:dashboard.status.pendingReview'),
  }
  return labels[status]
}

const CHECKLISTS_TYPE_COLOR_MAP: Record<ChecklistType, BadgeColorKeys> = {
  inspection: 'purple',
  task: 'neutral',
}

export const getChecklistTypeColor = (type: ChecklistType) => {
  return CHECKLISTS_TYPE_COLOR_MAP[type]
}

export const getChecklistTypeLabel = (
  type: ChecklistType,
  t: TFunction<['checklists']>,
): string => {
  const labels: Record<ChecklistType, string> = {
    inspection: t('checklists:dashboard.type.inspection'),
    task: t('checklists:dashboard.type.task'),
  }
  return labels[type]
}

const CHECKLISTS_BULK_STATUS_MAP: Record<ChecklistStatusExtended, ChecklistBulkStatus[]> = {
  all: ['completed', 'not-started'],
  completed: ['not-started'],
  'not-started': ['completed'],
  'past-due': ['completed'],
  'pending-review': ['completed'],
}

export const getChecklistBulkStatuses = (tab: ChecklistStatusExtended | undefined) => {
  return tab ? CHECKLISTS_BULK_STATUS_MAP[tab] : []
}

export const getChecklistBulkStatusAction = (
  status: ChecklistBulkStatus,
  t: TFunction<['checklists']>,
): { icon: Icon; label: string } => {
  const actions: Record<ChecklistBulkStatus, { icon: Icon; label: string }> = {
    completed: {
      icon: CheckIcon,
      label: t('checklists:dashboard.bulkActions.statusChange.completeLabel'),
    },
    'not-started': {
      icon: ArrowCounterClockwiseIcon,
      label: t('checklists:dashboard.bulkActions.statusChange.notStartedLabel'),
    },
  }
  return actions[status]
}

export const getChecklistBulkStatusConfirmationText = (
  status: ChecklistBulkStatus,
  count: number,
  t: TFunction<['checklists']>,
) => {
  const statusLabel = getChecklistStatusLabel(status, t)

  return t('checklists:dashboard.bulkActions.statusChange.confirmBody', {
    count,
    status: statusLabel,
  })
}

interface UseDefaultFilteringProps {
  searchParams: ReturnType<typeof useSearchParams>
  filters: ReturnType<typeof useFilters<ChecklistsFilters>>
}

// Note: Apply the default sort (newest first) on mount, only if none came from the URL
export const useDefaultFiltering = ({ searchParams, filters }: UseDefaultFilteringProps) => {
  useEffect(() => {
    if (searchParams.parsedQuery.sortBy || searchParams.parsedQuery.sortOrder) {
      return
    }

    filters.setFilters((prev) => ({
      ...prev,
      sortBy: 'dueDate',
      sortOrder: 'desc',
    }))
  }, [])
}
